import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserMinus, UserPlus, Printer, BookOpenCheck, CalendarDays, ArrowLeft, GraduationCap, Building2 } from "lucide-react";
import { getStats } from "../lib/api";
import { SCHOOL_LOGO, SCHOOL_NAME, CURRENT_ACADEMIC_YEAR, STAGES, STAGE_KEYS, can } from "../lib/constants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";

const COLORS = ["#0F766E", "#14B8A6", "#2DD4BF", "#0D9488", "#115E59", "#5EEAD4", "#0EA5E9", "#D4AF37", "#A67C00"];
const STAGE_COLORS = { primary: "#0F766E", middle: "#0D9488", high: "#D4AF37" };

function StatCard({ label, value, icon: Icon, tone = "teal", testid }) {
  const tones = {
    teal: "from-teal-600 to-teal-700",
    red: "from-rose-500 to-rose-600",
    gold: "from-amber-500 to-yellow-600",
    slate: "from-slate-700 to-slate-800",
    indigo: "from-indigo-600 to-indigo-700",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tones[tone]} text-white p-4 sm:p-6 shadow-lg`} data-testid={testid}>
      <div className="absolute -left-6 -bottom-6 w-24 sm:w-28 h-24 sm:h-28 rounded-full bg-white/10" />
      <div className="absolute -right-8 -top-8 w-20 sm:w-24 h-20 sm:h-24 rounded-full bg-white/10" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs sm:text-sm opacity-90 font-cairo leading-snug">{label}</div>
          <div className="mt-1.5 sm:mt-2 text-3xl sm:text-4xl font-extrabold tabular-nums tracking-tight">{value}</div>
        </div>
        <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
          <Icon size={18} className="sm:hidden" />
          <Icon size={22} className="hidden sm:block" />
        </div>
      </div>
    </div>
  );
}

function QuickAction({ to, label, icon: Icon, testid }) {
  return (
    <Link to={to} data-testid={testid} className="btn-elevate group flex items-center justify-between gap-3 rounded-xl bg-white border border-slate-200 p-4 hover:border-teal-300">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center group-hover:bg-teal-700 group-hover:text-white transition-colors">
          <Icon size={18} />
        </div>
        <span className="font-medium text-slate-800">{label}</span>
      </div>
      <ArrowLeft size={16} className="text-slate-400 group-hover:text-teal-700" />
    </Link>
  );
}

export default function Dashboard() {
  const { user, currentStage, setCurrentStage } = useAuth();
  const [stats, setStats] = useState(null);
  const stage = currentStage;
  const setStage = setCurrentStage;

  useEffect(() => {
    const params = stage && stage !== "all" ? { stage } : {};
    getStats(params).then(setStats).catch(() => setStats({ total_new: 0, total_withdrawn: 0, net: 0, by_grade: [], by_stage: [], academic_years: [] }));
  }, [stage]);

  const gradeData = stats?.by_grade || [];
  const pieData = (stats?.by_stage || []).filter(s => s.new > 0).map(s => ({ name: s.label, value: s.new, key: s.key }));
  const role = user?.role;

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-white border border-slate-200">
        <div className="absolute inset-0 bg-gradient-to-l from-teal-700 via-teal-700 to-teal-800" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0px, transparent 200px), radial-gradient(circle at 80% 60%, rgba(212,175,55,0.3) 0px, transparent 250px)" }} />
        <div className="relative flex items-center gap-4 sm:gap-6 p-4 sm:p-7 lg:p-9">
          <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-xl sm:rounded-2xl bg-white p-1.5 sm:p-2 shadow-xl shrink-0">
            <img src={SCHOOL_LOGO} alt="logo" className="w-full h-full object-contain" />
          </div>
          <div className="text-white min-w-0 flex-1">
            <div className="text-[10px] sm:text-xs lg:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] text-teal-100 font-cairo">المملكة العربية السعودية</div>
            <h1 className="font-cairo text-xl sm:text-3xl lg:text-4xl font-extrabold mt-1 sm:mt-1.5 leading-tight">{SCHOOL_NAME}</h1>
            <div className="mt-1 text-teal-50/90 text-[11px] sm:text-sm">نظام إدارة الطلاب — العام الدراسي {CURRENT_ACADEMIC_YEAR}</div>
            <div className="mt-2 sm:mt-3 hidden sm:flex items-center gap-2 text-amber-300 text-xs">
              <span className="inline-block w-8 sm:w-12 h-px bg-amber-300/70" />
              <span className="font-cairo">العلم النافع طريق النجاح</span>
              <span className="inline-block w-8 sm:w-12 h-px bg-amber-300/70" />
            </div>
          </div>
        </div>
      </div>

      {/* Stage filter */}
      <Card className="p-3 sm:p-4 border-slate-200 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-slate-700 font-cairo font-bold text-sm">
          <Building2 size={18} className="text-teal-700" />
          <span>اعرض البيانات حسب المرحلة:</span>
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="h-10 bg-white w-full sm:w-[260px]" dir="rtl" data-testid="stage-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent dir="rtl">
            <SelectItem value="all">جميع المراحل</SelectItem>
            {STAGE_KEYS.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <StatCard label="إجمالي الطلاب المستجدين" value={stats?.total_new ?? "—"} icon={UserPlus} tone="teal" testid="stat-new" />
        <StatCard label="إجمالي الطلاب المنسحبين" value={stats?.total_withdrawn ?? "—"} icon={UserMinus} tone="red" testid="stat-withdrawn" />
        <StatCard label="عدد الأعوام المسجلة" value={stats?.academic_years?.length ?? 0} icon={CalendarDays} tone="gold" testid="stat-years" />
      </div>

      {/* Stage breakdown cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        {(stats?.by_stage || STAGE_KEYS.map(k => ({ key: k, label: STAGES[k], new: 0, withdrawn: 0 }))).map(s => (
          <Card key={s.key} className="p-4 border-slate-200 relative overflow-hidden" data-testid={`stage-card-${s.key}`}>
            <div className="absolute right-0 top-0 w-1.5 h-full" style={{ backgroundColor: STAGE_COLORS[s.key] }} />
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><GraduationCap size={14} /> {s.label}</div>
                <div className="flex items-end gap-3">
                  <div>
                    <div className="text-[11px] text-slate-500">مستجدون</div>
                    <div className="text-2xl font-extrabold text-teal-700 tabular-nums">{s.new}</div>
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                  <div>
                    <div className="text-[11px] text-slate-500">منسحبون</div>
                    <div className="text-2xl font-extrabold text-rose-600 tabular-nums">{s.withdrawn}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        <Card className="lg:col-span-2 p-4 sm:p-5 border-slate-200">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900">توزيع الطلاب حسب الصفوف</h3>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">مقارنة بين المستجدين والمنسحبين</p>
            </div>
            <BookOpenCheck className="text-teal-700 shrink-0" size={18} />
          </div>
          <div className="h-72 sm:h-80 w-full" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gradeData} margin={{ top: 8, right: 8, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="grade" tick={{ fontSize: 9, fill: "#475569" }} angle={-40} textAnchor="end" height={90} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#475569" }} width={28} />
                <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontFamily: "Tajawal", fontSize: 12 }} formatter={(v) => v === "new" ? "مستجدون" : "منسحبون"} />
                <Bar dataKey="new" fill="#0F766E" radius={[6, 6, 0, 0]} />
                <Bar dataKey="withdrawn" fill="#EF4444" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 sm:p-5 border-slate-200">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900">نسبة المستجدين حسب المراحل</h3>
            <Users className="text-teal-700 shrink-0" size={18} />
          </div>
          <div className="h-72 sm:h-80 w-full" dir="ltr">
            {pieData.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
                <Users size={40} />
                <span>لا توجد بيانات بعد</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={90} paddingAngle={3} label>
                    {pieData.map((d) => <Cell key={d.key} fill={STAGE_COLORS[d.key] || COLORS[0]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Quick actions */}
      <Card className="p-4 sm:p-5 border-slate-200">
        <h3 className="font-cairo text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4">إجراءات سريعة</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {can.createStudent(user) && <QuickAction to="/new/register" label="تسجيل طالب مستجد" icon={UserPlus} testid="qa-new-register" />}
          {can.createStudent(user) && <QuickAction to="/withdrawn/register" label="تسجيل طالب منسحب" icon={UserMinus} testid="qa-withdrawn-register" />}
          <QuickAction to="/new" label="عرض المستجدين" icon={Users} testid="qa-new-list" />
          {can.viewReports(user) && <QuickAction to="/reports" label="التقارير والطباعة" icon={Printer} testid="qa-reports" />}
        </div>
      </Card>
    </div>
  );
}
